import { useRef } from 'react';

import { RouteManager } from '../../RouteManager';
import { ToastManager } from '../../ToastManager';

import { userMeDraftStore } from '../../store/users/UserMeDraftStore.ts';

import { useIsAltPressed } from '../../hooks/useAltKeyPressed';
import { useI18n } from '../../hooks/useI18nFormatters';
import { useAdvancedFlow } from '../../store/advanced-flow/useAdvancedFlows.ts';
import { useApi } from '../../store/SelectedHomeyStore.hooks.ts';
import { useUserMe } from '../../store/users/useUserMe.ts';

import { glyph } from '../../theme/glyph';
import { theme } from '../../theme/theme';

import { ContextMenuContent } from '../../components/common/context-menu/ContextMenuContent';
import { DeveloperMenu } from '../../components/common/context-menu/DeveloperMenu';
import { promptMessage } from '../../components/overlay/DialogPrompt';

export function FlowNavigationItemAdvancedFlowContextMenuContent(props) {
  const { i18n } = useI18n();
  // const ctx = useContextMenuContext();

  const isAltPressed = useIsAltPressed();

  const pendingRef = useRef({
    favorite: false,
  });
  const { api } = useApi();
  const { advancedFlow } = useAdvancedFlow({ advancedFlowId: props.nodeId });

  const { userMe } = useUserMe();
  const isFavoriteFlow = userMe?.properties.favoriteFlows?.includes(advancedFlow?.id) ?? false;

  const toggleText = advancedFlow.enabled
    ? i18n.messageFormatter(`flow.disable`)
    : i18n.messageFormatter(`flow.enable`);

  function onAction(key) {
    (async () => {
      switch (key) {
        case 'trigger': {
          try {
            const result = await api.flow.triggerAdvancedFlow({ id: props.nodeId, state: {} });
            ToastManager.add({
              icon: glyph.system.advancedFlow,
              message: i18n.messageFormatter(`flow.startedAdvancedFlow`, {
                name: advancedFlow.name,
              }),
            });
            console.log(result);
          } catch (error) {
            console.error(error);
            ToastManager.handleError(error);
          }

          break;
        }

        case 'toggle':
          api.flow
            .updateAdvancedFlow({
              id: advancedFlow.id,
              advancedflow: { enabled: !advancedFlow.enabled },
            })
            .catch(ToastManager.handleError);
          break;

        case 'favorite': {
          (async () => {
            const favoriteName = `**${advancedFlow.name}**`;

            if (pendingRef.current.favorite === true) {
              ToastManager.add({
                message: 'Request still pending, please wait...',
              });
              return;
            }

            if (isFavoriteFlow) {
              try {
                pendingRef.current.favorite = true;

                const nextUserMe = {
                  ...userMe,
                  properties: {
                    ...userMe.properties,
                    favoriteFlows: (userMe.properties.favoriteFlows ?? []).filter(
                      (id) => id !== props.nodeId,
                    ),
                  },
                };
                userMeDraftStore.setUserMeDraft({ userMe: nextUserMe });

                await api.users.updateUserMeProperties({
                  id: 'favoriteFlows',
                  value: nextUserMe.properties.favoriteFlows,
                });

                ToastManager.add({
                  icon: glyph.core.heartOutline,
                  iconColor: theme.icon.color_light,
                  message: i18n.messageFormatter(`flow.removeFavorite`, {
                    name: favoriteName,
                  }),
                });
                userMeDraftStore.setUserMeDraftDone();
              } catch (error) {
                userMeDraftStore.setUserMeDraftFailed();
                ToastManager.handleError(error);
              } finally {
                pendingRef.current.favorite = false;
              }

              return;
            }

            try {
              pendingRef.current.favorite = true;

              const nextUserMe = {
                ...userMe,
                properties: {
                  ...userMe.properties,
                  favoriteFlows: [...(userMe.properties.favoriteFlows ?? []), props.nodeId],
                },
              };
              userMeDraftStore.setUserMeDraft({ userMe: nextUserMe });

              await api.users.updateUserMeProperties({
                id: 'favoriteFlows',
                value: nextUserMe.properties.favoriteFlows,
              });

              ToastManager.add({
                icon: glyph.core.heartFill,
                iconColor: theme.color.special_favorite,
                message: i18n.messageFormatter(`flow.addFavorite`, {
                  name: favoriteName,
                }),
              });
              userMeDraftStore.setUserMeDraftDone();
            } catch (error) {
              userMeDraftStore.setUserMeDraftFailed();
              ToastManager.handleError(error);
            } finally {
              pendingRef.current.favorite = false;
            }
          })().catch(ToastManager.handleError);
          break;
        }

        case 'duplicate':
          try {
            const result = await api.flow.createAdvancedFlow({
              advancedflow: {
                name: `${advancedFlow.name} (${i18n.messageFormatter('common.copyNoun')})`,
                folder: advancedFlow.folder,
                enabled: advancedFlow.enabled,
                cards: advancedFlow.cards,
              },
            });

            RouteManager.toAdvancedFlow(result.id);
          } catch (error) {
            ToastManager.handleError(error);
          }

          break;

        case 'export': {
          const filename = `${advancedFlow.name || advancedFlow.id}.homeyadvflow`;
          const filedata = JSON.stringify({
            type: 'advanced',
            name: advancedFlow.name,
            cards: advancedFlow.cards,
          });

          // Use the File System Access API if available, otherwise fallback to Blob
          if (window.showSaveFilePicker) {
            Promise.resolve()
              .then(async () => {
                const handle = await window.showSaveFilePicker({
                  suggestedName: filename,
                  types: [
                    {
                      description: 'Advanced Flow Export',
                      accept: { 'application/json': ['.homeyadvflow'] },
                    },
                  ],
                });

                const writable = await handle.createWritable();
                await writable.write(filedata);
                await writable.close();
              })
              .catch((err) => {
                if (err.name === 'AbortError') return;
                alert(`Error Exporting Advanced Flow: ${err.message}`);
              });
          } else {
            const blob = new Blob([filedata], { type: 'application/json' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = filename;
            a.click();
            URL.revokeObjectURL(url);
          }

          break;
        }
        case 'rename':
          props.onRenameRequest();
          break;
        case 'delete':
          try {
            await promptMessage({
              message: i18n.messageFormatter('prompt.delete', { name: advancedFlow.name }),
            });

            await api.flow.deleteAdvancedFlow({
              id: advancedFlow.id,
            });

            // Reroute if this Flow was selected.
            if (RouteManager.isActiveFlow(advancedFlow.id)) {
              RouteManager.toFlows({ skipUnsavedChangesCheck: true });
            }
          } catch {}
          break;
        default:
          break;
      }
    })().catch(console.error);
  }

  return (
    <ContextMenuContent.List>
      <ContextMenuContent.ListItem
        label={i18n.messageFormatter('flow.start')}
        icon={glyph.core.play}
        isDisabled={advancedFlow?.triggerable !== true}
        onPress={() => {
          onAction('trigger');
        }}
      />

      <ContextMenuContent.ListItem
        label={toggleText}
        onPress={() => {
          onAction('toggle');
        }}
      />

      <ContextMenuContent.ListItem
        label={i18n.messageFormatter('common.rename')}
        onPress={() => {
          onAction('rename');
        }}
      />

      <ContextMenuContent.Divider />

      <ContextMenuContent.ListItem
        label={i18n.messageFormatter('flow.favorite')}
        icon={isFavoriteFlow ? glyph.core.heartFill : glyph.core.heartOutline}
        onPress={() => {
          onAction('favorite');
        }}
      />

      <ContextMenuContent.ListItem
        label={i18n.messageFormatter('common.duplicate')}
        icon={glyph.core.squareFilledOnSquare}
        onPress={() => {
          onAction('duplicate');
        }}
      />

      {isAltPressed === true && (
        <ContextMenuContent.ListItem
          label={i18n.messageFormatter('flow.export')}
          icon={glyph.core.arrowDownToLine}
          onPress={() => {
            onAction('export');
          }}
        />
      )}

      <DeveloperMenu onIdRequest={() => advancedFlow?.id} onResourceRequest={() => advancedFlow} />

      <ContextMenuContent.Divider />

      <ContextMenuContent.ListItem
        label={i18n.messageFormatter('common.delete')}
        variant="danger"
        icon={glyph.core.trashFill}
        onPress={() => {
          onAction('delete');
        }}
      />
    </ContextMenuContent.List>
  );
}
